import React, { useEffect, useState } from "react";
import { Trash2, Search, Library as LibraryIcon, Copy, X, FileText, Send, Download, Globe, Sparkles, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const TYPES = ["all", "artist_bio", "social_caption", "podcast_title", "podcast_description", "podcast_show_notes", "podcast_seo_keywords", "podcast_hashtags", "podcast_summary"];

export default function Library() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);  // currently-viewed item, or null
  const [sending, setSending] = useState(null);  // destination key while busy
  const [languages, setLanguages] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = type !== "all" ? `?type=${type}` : "";
      const res = await api.get(`/content${params}`);
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [type]);
  useEffect(() => { api.get("/i18n/languages").then((r) => setLanguages(r.data)).catch(() => {}); }, []);

  const filtered = items.filter((i) =>
    !q || i.output.toLowerCase().includes(q.toLowerCase()) || (i.prompt || "").toLowerCase().includes(q.toLowerCase())
  );

  const del = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/content/${id}`);
      setItems(items.filter((i) => i.content_id !== id));
      if (detail?.content_id === id) setDetail(null);
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(label);
    } catch { toast.error("Copy failed"); }
  };

  // Download the output as a .txt file (purely client-side)
  const downloadTxt = (item) => {
    const safeType = (item.type || "content").replace(/[^a-z0-9]+/gi, "_");
    const ts = new Date(item.created_at).toISOString().slice(0, 10);
    const blob = new Blob([item.output || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bfa-${safeType}-${ts}.txt`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 500);
    toast.success("Downloaded as .txt");
  };

  // Send to AI Studio for editing — drop the prompt + output into sessionStorage and navigate
  const sendToAIStudio = (item) => {
    sessionStorage.setItem("bfa:import_ai_studio", JSON.stringify({
      type: item.type, prompt: item.prompt || "", output: item.output || "", from_content_id: item.content_id,
    }));
    toast.success("Sent to AI Studio");
    setDetail(null);
    navigate("/dashboard/generate?imported=1");
  };

  // Send to Now Playing as a draft social post
  const sendToNowPlaying = (item) => {
    sessionStorage.setItem("bfa:import_nowplaying_draft", JSON.stringify({
      caption: item.output || "", platform: item.platform || "instagram", from_content_id: item.content_id,
    }));
    toast.success("Drafted for Now Playing");
    setDetail(null);
    navigate("/dashboard/nowplaying?imported=1");
  };

  // Translate the current item into one specific target language (creates a new content row)
  const translateTo = async (langCode) => {
    if (!detail) return;
    setSending(`translate-${langCode}`);
    try {
      const { data } = await api.post(`/content/${detail.content_id}/translate`, { target_languages: [langCode] });
      const created = (data.created || [])[0];
      toast.success(`Translated to ${created?.language_name || langCode} — saved as new Library entry`);
      // Refresh the grid so the new row shows up
      load();
      setDetail(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed", { duration: 8000 });
    } finally { setSending(null); }
  };

  // Publish straight to the connected WordPress as a draft post
  const publishToWordPress = async (item) => {
    setSending("wordpress");
    try {
      const text = item.output || "";
      const firstLine = text.split("\n")[0].slice(0, 120) || "Imported from Content Library";
      // Convert plaintext to simple HTML paragraphs
      const html = text
        .trim()
        .split(/\n\s*\n/)
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("\n");
      const { data } = await api.post("/blog-connector/wordpress/publish", {
        headline: firstLine,
        content_html: html,
        status: "draft",
        tags: [item.type.replace(/_/g, " ")],
      });
      toast.success(`Published to WordPress as draft → ${data.post_url || data.id || ""}`);
      setDetail(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WordPress publish failed", { duration: 8000 });
    } finally { setSending(null); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="library-page">
      <PageHeader
        eyebrow="// CONTENT LIBRARY"
        title="Every word, archived."
        description="Search, filter, and re-use every AI generation you've ever shipped. Click any card to read the full story."
      />

      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search content..."
            className="pl-9 bg-bg-surface border-line"
            data-testid="library-search"
          />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="sm:w-64 bg-bg-surface border-line" data-testid="library-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-bg-elevated border-line">
            {TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-ink-muted font-mono text-xs">LOADING...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-ink-muted">
          <LibraryIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">No content yet. Generate some in the AI Studio.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="library-grid">
          {filtered.map((c) => (
            <button
              key={c.content_id}
              type="button"
              onClick={() => setDetail(c)}
              className="text-left rounded-xl border border-line bg-bg-surface p-5 lift transition hover:border-brand-amber/50 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-amber/60"
              data-testid={`lib-item-${c.content_id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="rounded-full bg-brand-amber/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                  {c.type.replace(/_/g, " ")}
                </span>
                {c.platform && (
                  <span className="rounded-full bg-bg-elevated px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-secondary border border-line">
                    {c.platform}
                  </span>
                )}
                <span className="ml-auto text-xs text-ink-muted font-mono">{new Date(c.created_at).toLocaleDateString()}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => del(c.content_id, e)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") del(c.content_id, e); }}
                  className="text-ink-muted hover:text-brand-onair transition cursor-pointer"
                  data-testid={`lib-delete-${c.content_id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </div>
              <p className="text-sm text-ink-primary line-clamp-4 whitespace-pre-wrap">{c.output}</p>
              {c.prompt && (
                <div className="mt-3 pt-3 border-t border-line">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Prompt</div>
                  <p className="text-xs text-ink-secondary line-clamp-2">{c.prompt}</p>
                </div>
              )}
              <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-cyan-300/70 inline-flex items-center gap-1">
                <FileText className="h-3 w-3" /> Click to read full
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Full-text detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent
          className="bg-bg-surface border-line text-ink-primary max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="library-detail-modal"
        >
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="rounded-full bg-brand-amber/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                    {detail.type.replace(/_/g, " ")}
                  </span>
                  {detail.platform && (
                    <span className="rounded-full bg-bg-elevated px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-secondary border border-line">
                      {detail.platform}
                    </span>
                  )}
                  <span className="text-[11px] text-ink-muted font-mono">
                    {new Date(detail.created_at).toLocaleString()}
                  </span>
                </div>
                <DialogTitle className="font-display text-xl pt-2" data-testid="library-detail-title">
                  {(detail.output || "").split("\n")[0].slice(0, 100) || "Generated content"}
                </DialogTitle>
              </DialogHeader>

              <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-5 mt-2">
                <section data-testid="library-detail-output-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber">
                      // FULL OUTPUT
                    </div>
                    <button
                      onClick={() => copy(detail.output, "Output copied")}
                      className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 transition"
                      data-testid="library-detail-copy-output"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  <div
                    className="rounded-xl border border-line bg-bg-elevated p-4 text-sm whitespace-pre-wrap leading-relaxed text-ink-primary"
                    data-testid="library-detail-output"
                  >
                    {detail.output || <span className="italic text-ink-muted">(empty)</span>}
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-ink-muted text-right">
                    {(detail.output || "").length} chars · {(detail.output || "").split(/\s+/).filter(Boolean).length} words
                  </div>
                </section>

                {detail.prompt && (
                  <section data-testid="library-detail-prompt-section">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
                        // ORIGINAL PROMPT
                      </div>
                      <button
                        onClick={() => copy(detail.prompt, "Prompt copied")}
                        className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 transition"
                        data-testid="library-detail-copy-prompt"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                    </div>
                    <div className="rounded-xl border border-line bg-bg p-4 text-xs whitespace-pre-wrap leading-relaxed text-ink-secondary">
                      {detail.prompt}
                    </div>
                  </section>
                )}

                {(detail.metadata && Object.keys(detail.metadata).length > 0) && (
                  <section>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted mb-2">
                      // METADATA
                    </div>
                    <div className="rounded-xl border border-line bg-bg p-4 text-[11px] font-mono text-ink-secondary overflow-x-auto">
                      <pre>{JSON.stringify(detail.metadata, null, 2)}</pre>
                    </div>
                  </section>
                )}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-line">
                <button
                  onClick={() => copy(detail.output, "Output copied")}
                  data-testid="library-detail-copy-all"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy output
                </button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="library-detail-send-to"
                      disabled={!!sending}
                      className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/20 transition disabled:opacity-50"
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Send to...
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-bg-surface border-line text-ink-primary w-64">
                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      // REPURPOSE
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => sendToAIStudio(detail)}
                      data-testid="library-send-ai-studio"
                      className="cursor-pointer focus:bg-bg-elevated"
                    >
                      <Sparkles className="h-4 w-4 mr-2 text-brand-amber" />
                      <div>
                        <div className="text-sm">AI Studio (edit)</div>
                        <div className="text-[10px] text-ink-muted">Re-generate or refine</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => sendToNowPlaying(detail)}
                      data-testid="library-send-nowplaying"
                      className="cursor-pointer focus:bg-bg-elevated"
                    >
                      <Send className="h-4 w-4 mr-2 text-cyan-300" />
                      <div>
                        <div className="text-sm">Now Playing draft</div>
                        <div className="text-[10px] text-ink-muted">Use as a social post draft</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      // PUBLISH
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => publishToWordPress(detail)}
                      data-testid="library-send-wordpress"
                      className="cursor-pointer focus:bg-bg-elevated"
                    >
                      <Globe className="h-4 w-4 mr-2 text-emerald-300" />
                      <div>
                        <div className="text-sm">WordPress (draft)</div>
                        <div className="text-[10px] text-ink-muted">Publish to connected WP</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      // LOCALIZE
                    </DropdownMenuLabel>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        data-testid="library-translate-trigger"
                        className="cursor-pointer focus:bg-bg-elevated"
                      >
                        <Sparkles className="h-4 w-4 mr-2 text-cyan-300" />
                        <div className="flex-1">
                          <div className="text-sm">Translate to…</div>
                          <div className="text-[10px] text-ink-muted">Creates a localized copy in your Library</div>
                        </div>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="bg-bg-surface border-line text-ink-primary w-64 max-h-[60vh] overflow-y-auto">
                        {languages?.launch?.map((l) => (
                          <DropdownMenuItem
                            key={l.code}
                            onClick={() => translateTo(l.code)}
                            data-testid={`library-translate-${l.code}`}
                            disabled={sending === `translate-${l.code}`}
                            className="cursor-pointer focus:bg-bg-elevated flex items-center gap-2"
                          >
                            <span className="text-base">{l.flag}</span>
                            <span className="flex-1">
                              <span className="text-sm">{l.name}</span>
                              <span className="text-[10px] text-ink-muted ml-2">{l.native}</span>
                            </span>
                            {sending === `translate-${l.code}` && <Loader2 className="h-3 w-3 animate-spin" />}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="bg-line" />
                        <DropdownMenuLabel className="text-[9px] font-mono uppercase tracking-wider text-cyan-300">
                          Caribbean · USP
                        </DropdownMenuLabel>
                        {languages?.caribbean?.map((l) => (
                          <DropdownMenuItem
                            key={l.code}
                            onClick={() => translateTo(l.code)}
                            data-testid={`library-translate-${l.code}`}
                            disabled={sending === `translate-${l.code}`}
                            className="cursor-pointer focus:bg-bg-elevated flex items-center gap-2"
                          >
                            <span className="text-base">{l.flag}</span>
                            <span className="flex-1">
                              <span className="text-sm">{l.name}</span>
                              <span className="text-[10px] text-ink-muted ml-2">{l.native}</span>
                            </span>
                            {sending === `translate-${l.code}` && <Loader2 className="h-3 w-3 animate-spin" />}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="bg-line" />
                        <DropdownMenuLabel className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">
                          Phase 1
                        </DropdownMenuLabel>
                        {languages?.phase1?.map((l) => (
                          <DropdownMenuItem
                            key={l.code}
                            onClick={() => translateTo(l.code)}
                            data-testid={`library-translate-${l.code}`}
                            disabled={sending === `translate-${l.code}`}
                            className="cursor-pointer focus:bg-bg-elevated flex items-center gap-2"
                          >
                            <span className="text-base">{l.flag}</span>
                            <span className="flex-1">
                              <span className="text-sm">{l.name}</span>
                              <span className="text-[10px] text-ink-muted ml-2">{l.native}</span>
                            </span>
                            {sending === `translate-${l.code}` && <Loader2 className="h-3 w-3 animate-spin" />}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      // EXPORT
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => downloadTxt(detail)}
                      data-testid="library-send-txt"
                      className="cursor-pointer focus:bg-bg-elevated"
                    >
                      <Download className="h-4 w-4 mr-2 text-ink-secondary" />
                      <div>
                        <div className="text-sm">Download as .txt</div>
                        <div className="text-[10px] text-ink-muted">Save to your device</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <button
                  onClick={(e) => del(detail.content_id, e)}
                  data-testid="library-detail-delete"
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-300 hover:bg-rose-500/20 transition"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
                <button
                  onClick={() => setDetail(null)}
                  data-testid="library-detail-close"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary transition"
                >
                  <X className="h-3.5 w-3.5" /> Close
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
